import type { TargetDatabaseName } from "@/base/types"
import type { RequiredPermissions } from "@/constants/permissions"
import type { CognitoRequestName } from "@/redux/auth.reducer"
import type { RootThunkConfig } from "@/redux/create-async-thunk"
import type { RootState } from "@/redux/root.reducer"
import type { AsyncThunkOptions } from "@reduxjs/toolkit"
import type { JsonObject } from "type-fest"

import { Auth } from "aws-amplify"
import camelcaseKeys from "camelcase-keys"
import snakecaseKeys from "snakecase-keys"

import { createAsyncThunk } from "@/redux/create-async-thunk"
import {
  getDemoFarmIdOrActiveFarmIdFromState,
  getIsDemoModeActiveFromState,
} from "@/redux/selectors"
import * as Sentry from "@sentry/react-native"

import { checkPermissions } from "./check-permissions"
import { ENVIRONMENT } from "./environment"
import { makeFileLogger } from "./logger"
import { trackEvent } from "./segment"

import type { RequestNameToHandler } from "./farmhq-api"

import type { RequestBlockedInDemo } from "./check-permissions"
import type { BackendRequestBody } from "./get-api-url"

export type DatabaseValue<T> = {
  [key in TargetDatabaseName]?: T
} & {
  defaultValue: T
}

export type BackendRequestName<
  T extends keyof RequestNameToHandler = keyof RequestNameToHandler,
> = T extends T ? T : never
export type AnyRequestName =
  | BackendRequestName
  | CognitoRequestName
  | "CreateSupportTicket"
  | "ExitDemoMode"
  | "GeocodeFarmAddress"
  | "ReadLocalStorage"
  | "SaveLocalValues"
  | "SetActiveFarmId"

export type GetActionArgumentsType<N extends BackendRequestName> = N extends N
  ? RequestNameToHandler[N]["actionArguments"]
  : never
export type GetResponseDataType<N extends BackendRequestName> = N extends N
  ? RequestNameToHandler[N]["responseData"]
  : never

export type GetRequestBodyType<N extends BackendRequestName> =
  BackendRequestBody<N, GetActionArgumentsType<N>>

export interface BackendRequest<A = undefined, R = undefined> {
  actionArguments: A
  responseData: R
}

const logger = makeFileLogger({
  fileName: "send-request.ts",
})

export class BadRequestError extends Error {
  statusCode: number
  // Present when the backend's structured error body included a
  // ``deviceReturnCode`` (currently only DeviceError); the toast
  // pipeline uses this for i18n lookup without parsing the message.
  deviceReturnCode?: number
  constructor({
    code,
    deviceReturnCode,
    message,
    requestedAction,
  }: {
    code: number
    deviceReturnCode?: number
    message: string
    requestedAction: AnyRequestName
  }) {
    if (!Boolean(message)) {
      message = `The following request failed: ${requestedAction}`
    }
    super(message)
    this.name = "BadRequestError"

    this.statusCode = code
    this.deviceReturnCode = deviceReturnCode
  }
}
export class AuthTokenRejectedError extends Error {
  name = "AuthTokenRejectedError"
  statusCode = 401
}

/**
 * The backend reached the device but it didn't respond in time (operational,
 * not a bug). Status 504 from backend/errors.py:DeviceTimeout. Surface to
 * users as "device offline — try again" rather than reporting to Sentry.
 */
export class DeviceTimeoutError extends Error {
  name = "DeviceTimeoutError"
  statusCode = 504
  constructor(message: string) {
    super(message || "The device did not respond. Please try again.")
  }
}

export class AccountDeactivatedError extends Error {
  name = "AccountDeactivatedError"
  constructor() {
    super("This account has been deleted.")
  }
}

/**
 * The HTTP request never made it onto the wire (or the response never
 * came back) — typically because the phone has no service. Distinct
 * from server-side errors so ``useErrorHandler`` can suppress the
 * toast when the offline indicator is already visible, and so Sentry
 * doesn't get flooded by background polls that fail while the user is
 * out of coverage.
 */
export class NetworkOfflineError extends Error {
  name = "NetworkOfflineError"
  statusCode = 0
  constructor(message?: string) {
    super(message ?? "Network unavailable.")
  }
}

export async function sendRequest<
  N extends BackendRequestName,
  A extends GetActionArgumentsType<N>,
>(
  requestedAction: N,
  {
    actionArguments,
    activeFarmId,
  }: {
    actionArguments: A
    activeFarmId: number | null | undefined
  },
): Promise<GetResponseDataType<N>> {
  try {
    const body: BackendRequestBody & { [key: string]: unknown } = {
      actionArguments,
      activeFarmId,
      extra: {
        appName: ENVIRONMENT.appName,
        appVersion: ENVIRONMENT.appVersion,
        isEndToEndTest: ENVIRONMENT.isEndToEndTest,
        nodeEnv: process.env.NODE_ENV,
        targetDatabaseName: ENVIRONMENT.targetDatabaseName,
      },
      requestedAction,
    }
    // TODO: How to get the token?
    const authSession = await Auth.currentSession()

    const idToken = authSession.getIdToken()

    // const ipAddress = await Network.getIpAddressAsync().catch((error) => {
    //   logger.info("Error getting IP address", error)
    // })

    // Backend request. Raw network failures (no service, DNS unreachable)
    // arrive here as a TypeError ("Network request failed" on RN, "Failed
    // to fetch" on web). Convert to NetworkOfflineError so the toast
    // pipeline can localize it and skip Sentry — offline isn't a bug.
    let response: Response
    try {
      response = await fetch(ENVIRONMENT.apiUrl, {
        body: JSON.stringify(snakecaseKeys(body, { deep: true })),
        headers: {
          "Authorization": idToken.getJwtToken(),
          "Content-Type": "application/json",
        },
        method: "POST",
      })
    } catch (fetchErr) {
      if (fetchErr instanceof TypeError) {
        throw new NetworkOfflineError(fetchErr.message)
      }
      throw fetchErr
    }

    /*
    {
      "sub": "fdc19d24-653f-484c-92a9-de02f1f26f84",
      "email_verified": true,
      "iss": "https://cognito-idp.us-west-2.amazonaws.com/us-west-2_izfCnYzur",
      "cognito:username": "fdc19d24-653f-484c-92a9-de02f1f26f84",
      "origin_jti": "18f1de6d-4772-4907-9d41-c10c1a893427",

      "event_id": "d0ad8904-3a28-466f-912d-5b6f6a6c64f9",
      "token_use": "id",
      "auth_time": 1722964150,
      "exp": 1724302371,
      "iat": 1724298771,
      "jti": "172452c5-bb59-4204-bdcc-b7e7a4f6cdcc",
      "email": "test@codafarmtech.com"
    }
    */
    const { email, sub } = idToken.decodePayload() as {
      email?: string
      sub?: string
    }

    if (response.ok) {
      const asJson = (await response.json()) as JsonObject | null | undefined
      // Check if the backend returned an account deactivated response
      if (asJson?.error === "ACCOUNT_DEACTIVATED") {
        throw new AccountDeactivatedError()
      }
      return camelcaseKeys(asJson ?? {}, {
        deep: true,
        exclude: ["address_line_1", "address_line_2"],
      }) as GetResponseDataType<N>
    }

    let error: Error
    let skipSentryCapture = false
    if (response.status === 401) {
      // Don't log this one to sentry
      error = new AuthTokenRejectedError(
        "Auth token was rejected by API gateway",
      )
      skipSentryCapture = true
    } else {
      // Try to extract the error message from the response body
      let errorMessage = response.statusText
      let deviceReturnCode: number | undefined
      try {
        const errorBody = await response.json()
        if (typeof errorBody?.errorMessage === "string") {
          errorMessage = errorBody.errorMessage
        } else if (typeof errorBody?.error === "string") {
          errorMessage = errorBody.error
        } else if (typeof errorBody?.message === "string") {
          errorMessage = errorBody.message
        }
        if (typeof errorBody?.deviceReturnCode === "number") {
          deviceReturnCode = errorBody.deviceReturnCode
        }
      } catch {
        // Response body is not JSON, fall back to statusText
      }
      if (response.status === 504) {
        // Device-offline timeouts surface as DeviceTimeoutError so callers
        // can show a friendly toast; don't spam Sentry with operational events.
        error = new DeviceTimeoutError(errorMessage)
        skipSentryCapture = true
      } else {
        error = new BadRequestError({
          code: response.status,
          deviceReturnCode,
          message: errorMessage,
          requestedAction,
        })
      }
    }

    /*
    export interface ScopeContext {
      user: User;
      level: Severity | SeverityLevel;
      extra: Extras;
      contexts: Contexts;
      fingerprint: string[];
      requestSession: RequestSession;
      tags: {
          [key: string]: Primitive;
      };
    }
    */
    if (!skipSentryCapture) {
      Sentry.captureException(error, {
        level: "error",
        tags: {
          active_farm_id: activeFarmId,
          requested_action: requestedAction,
        },
        user: {
          email,
          id: sub,
          username: email,
        },
      })
    }
    // Pass error to the catch block
    throw error
  } catch (e) {
    logger.error(e)
    // In a thunk, this will get automatically serialized to include name, message
    // If it it just a string, only message is included
    throw e
  } finally {
    trackEvent({
      // activeFarmId,
      name: "backend_request",
      requestedAction,
    })
  }
}

export function createHandler<
  N extends keyof RequestNameToHandler,
  A extends GetActionArgumentsType<N>,
  R extends GetResponseDataType<N>,
>(
  requestedAction: N,
  options?: {
    demoMode?: { allowRequest?: boolean }
    requiredPermissions?:
      | RequiredPermissions
      | ((
          actionArguments: A,
          state: RootState,
        ) => RequiredPermissions | undefined)
    routeNameOverride?: Partial<DatabaseValue<string>>
    thunkOptions?: AsyncThunkOptions<A, RootThunkConfig>
    transformArguments?: (params: {
      actionArguments: A
      getState: () => RootState
    }) => A
  },
) {
  const {
    demoMode,
    requiredPermissions,
    routeNameOverride,
    thunkOptions,
    transformArguments,
  } = options ?? {}

  const defaultOptions: AsyncThunkOptions<A, RootThunkConfig> | undefined = {
    condition: (_, { getState }) => {
      const requests = getState().requests.entities
      const fetchStatus = requests[requestedAction]?.fetchStatus
      if (fetchStatus === "pending") {
        return false
      }
      return true
    },
  }
  return createAsyncThunk<R, A>(
    requestedAction,
    async (actionArguments, { getState, rejectWithValue }) => {
      if (typeof routeNameOverride === "string") {
        requestedAction = routeNameOverride as N
      }
      Sentry.setTag("requested_action", requestedAction)
      Sentry.setTag("target_db_name", ENVIRONMENT.targetDatabaseName)

      const state = getState()
      const activeFarmId = getDemoFarmIdOrActiveFarmIdFromState(state)

      const allowRequestInDemo = demoMode?.allowRequest === true
      // DENY REQUESTS THAT ARE MISSING PERMISSIONS

      let required: RequiredPermissions | undefined
      if (typeof requiredPermissions === "function") {
        required = requiredPermissions(actionArguments, state)
      } else {
        required = requiredPermissions
      }

      const missingPermissions = checkPermissions({
        provided: state.userPermissions.farmUserPermissions,
        required,
      })
      // Reducers can subscribe to this ("rejected with value")
      if (missingPermissions.code === "MISSING_PERMISSIONS") {
        return rejectWithValue(missingPermissions)
      }

      const isDemoModeEnabled = getIsDemoModeActiveFromState(state)

      // Some requests should be blocked in demo mode to prevent
      // changes to database state
      if (!allowRequestInDemo && isDemoModeEnabled) {
        const value: RequestBlockedInDemo = {
          code: "REQUEST_BLOCKED_IN_DEMO",
          requestedAction,
        }
        return rejectWithValue(value)
      }

      //Get the response from the backend
      const responseData = await sendRequest(requestedAction, {
        // Apply any dynamic changes to action arguments if needed
        actionArguments: transformArguments
          ? transformArguments({ actionArguments, getState })
          : actionArguments,
        activeFarmId,
      })

      return responseData as R
    },
    {
      ...defaultOptions,
      ...thunkOptions,
    },
  )
}
